/**
 * Session State Hook
 */

import { useState, useEffect } from 'react';
import type { SessionState } from '../types';
import { useEngageTrack } from './useEngageTrack';

/**
 * Hook to access and subscribe to session state changes
 * Updates every second to reflect current session state
 * 
 * @returns Current session state
 * 
 * @example
 * ```tsx
 * function SessionInfo() {
 *   const session = useSessionState();
 *   
 *   return (
 *     <div>
 *       <p>Session ID: {session.sessionId}</p>
 *       <p>Page Views: {session.pageViewCount}</p>
 *       <p>Status: {session.isActive ? 'Active' : 'Inactive'}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export const useSessionState = (): SessionState => {
  const { getSessionState } = useEngageTrack();
  const [sessionState, setSessionState] = useState<SessionState>(getSessionState());

  useEffect(() => {
    // Update session state every second
    const interval = setInterval(() => {
      setSessionState(getSessionState());
    }, 1000);

    return () => clearInterval(interval);
  }, [getSessionState]);

  return sessionState;
};
